import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@berky/ui'
import { ExternalLink } from 'lucide-react'

import { formatChangeDetailDate, type ChangeEntry } from './change-feed'

export function ChangeDetailDialog({
  entry,
  onOpenChange,
}: {
  entry: ChangeEntry | null
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={entry !== null} onOpenChange={onOpenChange}>
      {entry && (
        <DialogContent
          className="max-h-[90svh] overflow-y-auto sm:max-w-2xl"
          showCloseButton={false}
        >
          <DialogHeader>
            <p className="text-sm font-medium text-primary">{entry.area}</p>
            <DialogTitle className="pr-8 text-xl leading-snug">{entry.title}</DialogTitle>
            <DialogDescription>Die wichtigsten Neuerungen im Überblick</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <ChangeSummary
              value={
                entry.summary ??
                'Für diese Veröffentlichung ist noch keine ausführliche Beschreibung verfügbar.'
              }
            />
            <p className="border-t pt-4 text-xs text-muted-foreground">
              Veröffentlicht am {formatChangeDetailDate(entry.publishedAt)}
              {entry.author ? ` · Bearbeitet von ${entry.author}` : ''}
            </p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Schließen
              </Button>
            </DialogClose>
            {entry.pullRequestUrl && (
              <Button asChild>
                <a href={entry.pullRequestUrl} target="_blank" rel="noreferrer">
                  In Azure DevOps öffnen
                  <ExternalLink className="size-4" aria-hidden="true" />
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  )
}

type SummaryBlock =
  { type: 'paragraph' | 'heading'; text: string } | { type: 'list'; items: string[] }

function ChangeSummary({ value }: { value: string }) {
  const blocks = parseSummary(value)

  return (
    <div className="space-y-4 text-sm leading-relaxed text-foreground">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <h3 key={index} className="pt-1 font-semibold">
              {block.text}
            </h3>
          )
        }
        if (block.type === 'list') {
          return (
            <ul key={index} className="list-disc space-y-2 pl-5 marker:text-primary">
              {block.items.map((item, itemIndex) => (
                <li key={`${itemIndex}-${item}`}>{item}</li>
              ))}
            </ul>
          )
        }
        return <p key={index}>{block.text}</p>
      })}
    </div>
  )
}

function parseSummary(value: string): SummaryBlock[] {
  const blocks: SummaryBlock[] = []
  let paragraph: string[] = []
  let listItems: string[] = []

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
    paragraph = []
  }
  const flushList = () => {
    if (listItems.length === 0) return
    blocks.push({ type: 'list', items: listItems })
    listItems = []
  }

  for (const sourceLine of value.split('\n')) {
    const line = sourceLine.trim()
    if (!line) {
      flushParagraph()
      flushList()
      continue
    }

    const listItem = /^(?:[-*•]|\d+[.)])\s+(.+)$/u.exec(line)?.[1]
    if (listItem) {
      flushParagraph()
      listItems.push(normaliseInlineMarkdown(listItem))
      continue
    }

    flushList()
    const markdownHeading = /^#{1,6}\s+(.+)$/u.exec(line)?.[1]
    const plainHeading = line.endsWith(':') && line.length <= 80
    if (markdownHeading || plainHeading) {
      flushParagraph()
      blocks.push({
        type: 'heading',
        text: normaliseInlineMarkdown(markdownHeading ?? line),
      })
      continue
    }
    paragraph.push(normaliseInlineMarkdown(line))
  }

  flushParagraph()
  flushList()
  return blocks
}

function normaliseInlineMarkdown(value: string): string {
  return value
    .replace(/\[([^\]]+)\]\([^)]+\)/gu, '$1')
    .replace(/(?:\*\*|__)(.+?)(?:\*\*|__)/gu, '$1')
    .replace(/`([^`]+)`/gu, '$1')
}
